// ResizableIframeView.tsx
import React, { useRef, useState, CSSProperties } from 'react';
import { NodeViewWrapper, type NodeViewProps } from '@tiptap/react';

const MIN_WIDTH = 60;
const MIN_HEIGHT = 60;
const BORDER_COLOR = '#129';

const ResizableIframeView: React.FC<NodeViewProps> = ({ node, updateAttributes, selected }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [resizingStyle, setResizingStyle] = useState<Pick<CSSProperties, 'width' | 'height'>>();
  const url = new URL(node.attrs.src);
  const segments = url.pathname.split('/').filter(Boolean);
  const dynamicPart = segments[0];
  const keywords = [
    'Accordion',
    'tabs',
    'Button',
    'multiplechoice',
    'flipcard',
    'imageslider',
    'ClickAndRevealInteractions',
    'DragandDrop',
    'Hotspot',
  ];
  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    const direction = event.currentTarget.dataset.direction ?? 'se';
    const initialX = event.clientX;
    const initialY = event.clientY;

    const currentWidth = containerRef.current?.offsetWidth || node.attrs.width || 300;
    const currentHeight = containerRef.current?.offsetHeight || node.attrs.height || 200;

    let newWidth = currentWidth;
    let newHeight = currentHeight;

    const xTransform = direction[1] === 'w' ? -1 : 1;
    const yTransform = direction[0] === 'n' ? -1 : 1;

    const removeListeners = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', removeListeners);
      updateAttributes({ width: newWidth, height: newHeight });
      setResizingStyle(undefined);
    };

    const onMouseMove = (event: MouseEvent) => {
      newWidth = Math.max(currentWidth + xTransform * (event.clientX - initialX), MIN_WIDTH);
      newHeight = Math.max(currentHeight + yTransform * (event.clientY - initialY), MIN_HEIGHT);

      setResizingStyle({ width: newWidth, height: newHeight });
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', removeListeners);
  };

  const dragCornerButton = (direction: string) => (
    <div
      id={direction}
      role='button'
      tabIndex={0}
      onMouseDown={handleMouseDown}
      data-direction={direction}
      style={{
        position: 'absolute',
        height: '12px',
        width: '12px',
        backgroundColor: BORDER_COLOR,
        ...{ n: { top: -6 }, s: { bottom: -6 } }[direction[0]],
        ...{ w: { left: -6 }, e: { right: -6 } }[direction[1]],
        cursor: `${direction}-resize`,
        zIndex: 10,
      }}
    />
  );

  const alignment = node.attrs.align || 'left';
  const iframeHeight =
    typeof node.attrs.height === 'string' && node.attrs.height.includes('%')
      ? node.attrs.height
      : Math.max(60, parseInt(node.attrs.height, 10)) + 'px';
  const shouldSkipHeights =
    dynamicPart != null && keywords.some((word) => dynamicPart.includes(word));

  const handleWidth = (): string | number => {
    if (typeof node.attrs.width === 'string' && node.attrs.width.includes('%')) {
      const numericWidth = parseInt(node.attrs.width, 10);
      return numericWidth > 100 ? '100%' : node.attrs.width;
    }
    return node.attrs.width + 'px';
  };
  return (
    <NodeViewWrapper as='div'>
      <div
        style={{
         textAlign:
           alignment === 'center' ? 'center' : alignment === 'right' ? 'right' : 'left',
        }}
      >
        <div
          ref={containerRef}
          style={{
            display: 'inline-block',
            position: 'relative',
            width: resizingStyle?.width || handleWidth() || 300,
            ...(shouldSkipHeights
              ? {}
              : {
                  height: resizingStyle?.height || iframeHeight || 200,
                  minHeight: MIN_HEIGHT,
                }),

            minWidth: MIN_WIDTH,
            border: selected ? `2px solid ${BORDER_COLOR}` : 'none',
            boxSizing: 'border-box',
          }}
        >
          <iframe
            title={node.attrs.title || 'iframe'}
            ref={iframeRef}
            src={node.attrs.src}
            contentEditable={false}
            width="100%"
            height="100%"
            style={{
              pointerEvents:'none',
              width: '100%',
              height: '100%',
              display: 'block',
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          />
          {selected && ['nw', 'ne', 'sw', 'se'].map((dir) => dragCornerButton(dir))}
        </div>
      </div>
    </NodeViewWrapper>
  );
};

export default ResizableIframeView;
